import { Approval } from "../types";
import React from "react";
import { Box, Stack, Text, Title, Timeline } from "@mantine/core";
import {
    IconMessageDots,
    IconThumbDown,
    IconGitCommit,
    IconEye,
    IconThumbUp,
    IconStar,
    IconEdit,
    IconBookUpload,
    IconSend
} from "@tabler/icons-react";

interface CustomTimelineProps {
    approval: Approval[]
}

const CustomProductTimeline: React.FC<CustomTimelineProps> = ({ approval }) => {

    const getActionProperties = (action: any) => {
        let verb = "Unknown";
        let color = "gray";
        let icon = <IconMessageDots size={12} />;

        switch (action.type) {
            case "reject":
                verb = "Rejected";
                color = "red";
                icon = <IconThumbDown size={12} />;
                break;
            case "needs_update":
                verb = "Needs update";
                color = "yellow";
                icon = <IconGitCommit size={12} />;
                break;
            case "review":
                verb = "Reviewed";
                color = "cyan";
                icon = <IconEye size={12} />;
                break;
            case "selling_agent_review":
                verb = "Selling Agent Reviewed";
                color = "teal";
                icon = <IconEye size={12} />;
                break;
            case "needs_agent_update":
                verb = "CR Requesting updates from selling agent";
                color = "teal";
                icon = <IconEye size={12} />;
                break;
            case "operations_approve":
                verb = "Operations Approved";
                color = "indigo";
                icon = <IconEye size={12} />;
                break;
            case "authorized_signatory_approve":
                verb = "Authorized Signatory Approved";
                color = "indigo";
                icon = <IconEye size={12} />;
                break;
            case "execution":
                verb = action.level === "operations" ? "Execution (Operations)" : "Executed";
                color = action.level === "operations" ? "green" : "blue";
                icon = action.level === "operations" ? <IconThumbUp size={12} /> : <IconEye size={12} />;
                break;
            case "approve":
                verb = "Approved";
                color = action.level === "executive" ? "grape" : "green";
                icon = action.level === "executive" ? <IconStar size={12} /> : <IconThumbUp size={12} />;
                break;
            case "approval":
                verb = "Approval (CR)";
                color = "cyan";
                icon = <IconEye size={12} />;
                break;
            case "submit":
                verb = "Submitted";
                color = "blue";
                icon = <IconSend size={12} />;
                break;
            case "update":
                verb = "Updated";
                color = "blue";
                icon = <IconEdit size={12} />;
                break;
            case "upload_docs":
                verb = "Uploaded documents";
                color = "blue";
                icon = <IconBookUpload size={12} />;
                break;
            case "add_subaccount_id":
                verb = "Added Subaccount ID";
                color = "lime";
                icon = <IconEye size={12} />;
                break;
            case "activation":
                verb = "Activated";
                color = "violet";
                icon = <IconEye size={12} />;
                break;
            case "interview_form_data_added":
                verb = "Interview Form Data Added";
                color = "blue";
                icon = <IconEdit size={12} />;
                break;
            default:
                // Keep default values
                break;
        }

        return { verb, color, icon };
    };

    const tagsAsString = (tags: string[] | string) => {
        // Utility function for handling missing files display
        if (Array.isArray(tags)) {
            return tags.join(', ');
        }
        return tags;
    };

    const getActionKey = (action: Approval, index: number) => {
        const seconds = action.date?.seconds ?? "no-seconds";
        const nanoseconds = action.date?.nanoseconds ?? "no-nanos";
        return `${action.type ?? "unknown"}-${action.id ?? action.name ?? "unknown"}-${seconds}-${nanoseconds}-${index}`;
    };


    // Safety check for undefined or empty approval array
    if (!approval || approval.length === 0) {
        return (
            <Stack gap={0}>
                <Box mb="lg">
                    <Title order={3}>Actions</Title>
                </Box>
            </Stack>
        );
    }

    return (
        <Stack gap={0}>
            <Box mb="lg">
                <Title order={3}>Actions</Title>
            </Box>
            <Timeline
                active={100}
                bulletSize={24}
                lineWidth={2}
                reverseActive
            >
                {approval
                    ?.slice() // Create shallow copy to avoid mutating original array
                    .reverse() // Reverse so the latest action is at the top
                    .map((action: any, index: number) => {
                        const { verb, color, icon } = getActionProperties(action);

                        return (
                            <Timeline.Item
                                bullet={icon}
                                title={verb}
                                color={color}
                                key={getActionKey(action, index)}
                            >
                                <Text c="dimmed" size="sm">
                                    {`${verb} by ${action.name || 'Unknown'}`}
                                </Text>
                                {action.comments && (
                                    <Text c="violet" size="sm">
                                        {action.comments}
                                    </Text>
                                )}
                                {action.missingFiles && (
                                    <Text c="violet" size="sm">
                                        {tagsAsString(action.missingFiles)}
                                    </Text>
                                )}
                                <Text size="xs" mt={4}>
                                    {action.date?.seconds ?
                                        new Date(action.date.seconds * 1000).toLocaleString("en-GB", { hour12: true }) :
                                        'Date unavailable'
                                    }
                                </Text>
                            </Timeline.Item>
                        );
                    })}
            </Timeline>
        </Stack>
    );
};

export default CustomProductTimeline;
